/**
 * WCC360 v2 — Part 1 for the 12–14 "Explorers" band.
 *
 * Two screens, and neither of them scores anything.
 *
 * A twelve-year-old has no qualification, no salary expectation and no work
 * history. Every one of the adult Part 1 questions would be answered with a
 * guess, and that guess then becomes a hard filter that silently removes
 * careers. So Part 1 here collects context only: it makes the assessment
 * feel like it knows who's answering, and it colours the result copy.
 *
 * NEITHER screen writes a slot, touches the archetype tally, or filters the
 * career corpus. If you add scoring here, you have broken the band.
 */
import { ChoiceScreen, type Choice } from "../components/ChoiceScreen";
import { MultiSelect, type MultiSelectItem } from "../components/interactions/MultiSelect";
import { useAssessmentV2 } from "../store";
import type { SchoolYear } from "../types";

interface ScreenProps { onNext: () => void }

// -----------------------------------------------------------------------
// p1_school_year — context only
// -----------------------------------------------------------------------

export function SchoolYearScreen({ onNext }: ScreenProps) {
  const { state, setPart1 } = useAssessmentV2();

  const choices: readonly Choice<SchoolYear>[] = [
    { value: "6", label: "Class 6" },
    { value: "7", label: "Class 7" },
    { value: "8", label: "Class 8" },
    { value: "9", label: "Class 9" },
    { value: "other", label: "Something else" },
  ] as const;

  return (
    <ChoiceScreen
      title="Which class are you in?"
      subtitle="Just so we can talk to you properly. Nothing is scored here."
      choices={choices}
      value={state.part1.school_year}
      onSelect={(v) => setPart1({ school_year: v })}
      onContinue={onNext}
    />
  );
}


// -----------------------------------------------------------------------
// p1_what_you_like — context only, colours result copy
// -----------------------------------------------------------------------

export const LIKE_ITEMS: MultiSelectItem[] = [
  { id: "maths", label: "Maths" },
  { id: "science", label: "Science" },
  { id: "languages", label: "Languages and reading" },
  { id: "history", label: "History and the world" },
  { id: "art", label: "Art and design" },
  { id: "music", label: "Music" },
  { id: "sport", label: "Sport" },
  { id: "computers", label: "Computers and games" },
  { id: "building", label: "Building and fixing things" },
  { id: "animals", label: "Animals and nature" },
  { id: "helping", label: "Helping people" },
  { id: "acting", label: "Acting, dancing or performing" },
];

export function WhatYouLikeScreen({ onNext }: ScreenProps) {
  const { setPart1 } = useAssessmentV2();

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">
          Things you like
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium leading-tight">
          What do you enjoy, in school or out of it?
        </h1>
        <p className="mt-3 text-muted-foreground">
          Pick as many as you like. Liking something is not a promise.
        </p>
      </div>
      <MultiSelect
        items={LIKE_ITEMS}
        onComplete={(ids) => {
          // Stored for result colour only. Never a slot, never a filter.
          setPart1({ what_you_like: ids });
          onNext();
        }}
      />

    </div>
  );
}
